// Replace %variable% placeholders in invoice description / conditions / line text with the
// actual values pulled from the loaded Invoice + tenant + contact. Mirrors what the
// backend `Modules\Common\Services\StringVariablesReplacer` does when generating the
// preview HTML so users see the same rendered text in the lightweight artifact view.
//
// Canonical variable list pulled from the backend
// (Modules/Common/Services/StringVariablesReplacer.php +
// Modules/Invoicing/Services/InvoiceTemplateMaker/*).
//
// KNOWN DIVERGENCE — backend transformations not (yet) reproduced here:
//   - %client_identification_number% backend composes id + GLN + companyRegistrationNumber
//   - %document_type% backend translates via i18n; FE returns raw enum
//   - %document_purchase_order% / %document_buyer_reference% backend wraps with translated label
//   - %document_communication% backend uses getCommunication() (multi-country); FE = Belgian only
//   - %document_total_percentage@N% regex variable not implemented
//   - %label_*% variables not implemented (return as-is)
// If full parity becomes important, expose a backend `/render-text` endpoint instead of
// keeping two implementations in sync.
import type Invoice from '~/app/invoicing/models/Invoice';
import type Tenant from '~/app/auth/models/Tenant';

type Substitutions = Record<string, string>;

const formatDate = (m: { format: (s: string) => string } | null | undefined) =>
    m ? m.format('DD/MM/YYYY') : '';

const buildSubstitutions = (invoice: Invoice, tenant: Tenant | null, formatPrice: (n: number) => string): Substitutions => {
    const contact = invoice.contact;
    const worksite = invoice.worksite;
    const contactAddress = contact?.address ?? null;
    const contactBilling = contact?.billingAddress ?? null;
    const tenantAddress = tenant?.address ?? null;
    const tenantBilling = tenant?.billingAddress ?? null;

    const docNumber = invoice.number ?? '';
    const docDate = formatDate(invoice.trueDate);
    const docDue = formatDate(invoice.dueAt);
    const docTotal = formatPrice(invoice.total ?? 0);
    const docRemaining = formatPrice(invoice.totalDue ?? 0);
    const communication = invoice.belgianStructuredCommunication ?? '';
    const companyName = tenant?.name ?? '';

    return {
        // Document — canonical
        document_title: invoice.title ?? '',
        document_type: invoice.documentType ?? '',
        document_number: docNumber,
        document_date: docDate,
        document_dueat: docDue,
        document_total: docTotal,
        document_totalremaining: docRemaining,
        document_communication: communication,
        document_buyer_reference: invoice.buyerReference ?? '',
        document_purchase_order: invoice.purchaseOrder ?? '',

        // Document — type-agnostic short aliases
        documentnumber: docNumber,
        documentdate: docDate,
        documentamount: docTotal,
        documentremainingamount: docRemaining,

        // Document — type-specific aliases (all resolve to the active document data)
        invoicenumber: docNumber,
        quotenumber: docNumber,
        creditnotenumber: docNumber,
        orderslipnumber: docNumber,
        deliveryslipnumber: docNumber,

        invoicedate: docDate,
        quotedate: docDate,
        creditnotedate: docDate,
        orderslipdate: docDate,
        deliveryslipdate: docDate,

        invoiceamount: docTotal,
        quoteamount: docTotal,
        creditnoteamount: docTotal,
        orderslipamount: docTotal,
        deliveryslipamount: docTotal,

        invoiceremainingamount: docRemaining,
        quoteremainingamount: docRemaining,
        creditnoteremainingamount: docRemaining,
        orderslipremainingamount: docRemaining,
        deliveryslipremainingamount: docRemaining,

        // Date aliases used in templates
        duedate: docDue,
        validitydate: docDue,
        deliverydate: docDate,

        // Communication
        communication,

        // Company / tenant — Tenant model exposes a limited surface; the rest of the
        // %company_*% variables fall back to empty string when not on the model. The
        // backend has the full record server-side; FE shows a partial view.
        company_name: companyName,
        companyname: companyName,
        company_vat_number: tenant?.vatNumber ?? '',
        company_siret: tenant?.siret ?? '',
        company_email: tenant?.email ?? '',
        company_phone: tenant?.phone ?? '',
        company_address: addressDisplay(tenantAddress),
        company_address_street_name: addressField(tenantAddress, 'streetName'),
        company_address_street_number: addressField(tenantAddress, 'streetNumber'),
        company_address_street_box_number: addressField(tenantAddress, 'streetBoxNumber'),
        company_address_postal_code: addressField(tenantAddress, 'postalCode'),
        company_address_city: addressField(tenantAddress, 'city'),
        company_address_country: addressField(tenantAddress, 'country'),

        // Client
        client_company_or_name: contact?.displayName ?? '',
        client_name: contact?.displayName ?? '',
        client_first_name: contact?.firstName ?? '',
        client_last_name: contact?.lastName ?? '',
        client_honorific_prefix: contact?.honorificPrefix ?? '',
        client_company_name: contact?.companyName ?? '',
        client_identification_number: contact?.identificationNumber ?? '',
        client_vat_number: contact?.vatNumber ?? '',
        client_siret: contact?.siret ?? '',
        client_email: contact?.email ?? '',
        client_phone: contact?.phone ?? '',
        client_address_street_name: addressField(contactAddress, 'streetName'),
        client_address_street_number: addressField(contactAddress, 'streetNumber'),
        client_address_street_box_number: addressField(contactAddress, 'streetBoxNumber'),
        client_address_postal_code: addressField(contactAddress, 'postalCode'),
        client_address_city: addressField(contactAddress, 'city'),
        client_address_country: addressField(contactAddress, 'country'),
        client_billingaddress_street_name: addressField(contactBilling, 'streetName'),
        client_billingaddress_street_number: addressField(contactBilling, 'streetNumber'),
        client_billingaddress_street_box_number: addressField(contactBilling, 'streetBoxNumber'),
        client_billingaddress_postal_code: addressField(contactBilling, 'postalCode'),
        client_billingaddress_city: addressField(contactBilling, 'city'),
        client_billingaddress_country: addressField(contactBilling, 'country'),

        // Worksite (FE-only convenience; not part of the backend canonical list)
        worksite_title: worksite?.title ?? '',
        worksite_address: addressDisplay(worksite?.address),
    };
};

// Generic field reader that doesn't care about the exact Address class shape — the
// backend stores street/postal/city in attribute keys; we rely on those existing.
const addressField = (address: { attributes?: Record<string, unknown> } | null | undefined, key: string): string => {
    const value = address?.attributes?.[key];
    return typeof value === 'string' ? value : '';
};

const addressDisplay = (address: { display?: string } | null | undefined): string => {
    return address?.display ?? '';
};

export function replaceInvoiceVariables(text: string, invoice: Invoice, tenant: Tenant | null, formatPrice: (n: number) => string): string {
    if (!text) return '';
    const subs = buildSubstitutions(invoice, tenant, formatPrice);
    return text.replace(/%([a-z_]+)%/gi, (_match, key: string) => {
        const value = subs[key.toLowerCase()];
        return value !== undefined ? value : `%${key}%`;
    });
}
